// frontend/src/pages/ProcurementStrategyPage.tsx
// v2.0 - OMEGA PROCUREMENT (Drill-Down & Correction)

import { useState, FC, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
    Box, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Chip, Autocomplete, TextField, CircularProgress,
    Tooltip, IconButton, Collapse, Stack, LinearProgress, Button
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InventoryIcon from '@mui/icons-material/Inventory';
import AnalyticsIcon from '@mui/icons-material/Analytics'; 

import axiosInstance from '../config/axiosInstance';

// --- ESTILOS PREMIUM ---
const glassCardSx = {
    bgcolor: '#1e293b', 
    border: '1px solid #334155',
    borderRadius: 2,
    p: 0,
    overflow: 'hidden',
    boxShadow: 'none'
};

// --- TIPOS ---
interface Family {
    id: string;
    name: string;
    linked_ticker: string | null;
    alert_threshold: number;
    total_spend: number;
    sku_count: number;
}

interface MarketIndex {
    ticker: string;
    name: string;
    category: string;
    currency: string;
}

interface FamilySkuItem {
    sku: string;
    total_spend: number;
    avg_price: number;
    currency: string;
}

interface ProcurementStatus {
    total_spend_12m: number;
    has_data: boolean;
    total_records: number;
    last_upload_date: string | null;
    min_date: string | null; // NUEVO
    max_date: string | null; // NUEVO
}

// --- SUB-COMPONENTE: FILA DE FAMILIA (ROW) ---
const FamilyRow: FC<{ 
    row: Family; 
    indices: MarketIndex[]; 
    onUpdateTicker: (famId: string, ticker: string | null) => void;
    onAnalyze: (famId: string) => void; // <--- NUEVA PROP
    updating: boolean 
}> = ({ row, indices, onUpdateTicker, onAnalyze, updating }) => {
    const [open, setOpen] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    // 1. QUERY: Cargar SKUs (Solo si se despliega)
    const { data: skuDetails = [], isLoading: loadingSkus, refetch: refetchSkus } = useQuery({
        queryKey: ['familySkus', row.id],
        queryFn: async () => (await axiosInstance.get<FamilySkuItem[]>(`/procurement/families/${row.id}/skus`)).data,
        enabled: open, 
        staleTime: 0 
    });

    // 2. MUTATION: Desvincular SKU
    const unassignMutation = useMutation({
        mutationFn: async (sku: string) => {
            await axiosInstance.post('/procurement/skus/unassign', { skus: [sku] });
        },
        onSuccess: () => {
            enqueueSnackbar('SKU desvinculado.', { variant: 'success' });
            refetchSkus(); 
            queryClient.invalidateQueries({ queryKey: ['families'] }); 
        },
        onError: () => enqueueSnackbar('Error al desvincular SKU.', { variant: 'error' })
    });

    const selectedIndex = indices.find(i => i.ticker === row.linked_ticker) || null;

    return (
        <>
            <TableRow hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' }, borderBottom: '1px solid #334155' }}>
                {/* 1. CHEVRON DESPLEGABLE */}
                <TableCell width={50}>
                    <IconButton size="small" onClick={() => setOpen(!open)} sx={{ color: '#94a3b8' }}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>

                {/* 2. DATOS DE FAMILIA */}
                <TableCell sx={{ color: '#e2e8f0', fontWeight: 600 }}>{row.name}</TableCell>
                <TableCell align="right">
                    <Typography variant="body2" sx={{ color: '#38bdf8', fontWeight: 700, fontFamily: 'monospace' }}>
                        {row.total_spend.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                    </Typography>
                </TableCell>
                <TableCell align="center">
                    <Chip label={row.sku_count} size="small" icon={<InventoryIcon sx={{ fontSize: '0.8rem !important' }} />} sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: '#cbd5e1', border: '1px solid #475569' }} />
                </TableCell>

                {/* 3. SELECTOR DE MERCADO */}
                <TableCell sx={{ minWidth: 250 }}>
                    <Autocomplete
                        options={indices}
                        groupBy={(option) => option.category}
                        getOptionLabel={(option) => `${option.name} (${option.ticker})`}
                        value={selectedIndex}
                        isOptionEqualToValue={(opt, val) => opt.ticker === val.ticker}
                        onChange={(_, newValue) => {
                            onUpdateTicker(row.id, newValue ? newValue.ticker : null);
                        }}
                        disabled={updating}
                        renderInput={(params) => (
                            <TextField 
                                {...params} placeholder="Buscar índice..." variant="standard"
                                InputProps={{ ...params.InputProps, disableUnderline: true, sx: { color: '#f8fafc', fontSize: '0.9rem' } }}
                                sx={{ bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1, px: 1.5, py: 0.5, border: '1px solid', borderColor: row.linked_ticker ? '#4ade80' : '#475569', transition: 'border-color 0.3s' }}
                            />
                        )}
                        componentsProps={{ paper: { sx: { bgcolor: '#1e293b', border: '1px solid #334155', color: '#f8fafc' } } }}
                    />
                </TableCell>

                {/* 4. ICONO ESTADO */}
                <TableCell align="center">
                    {row.linked_ticker ? <Tooltip title="Conectado"><LinkIcon sx={{ color: '#4ade80' }} /></Tooltip> : <Tooltip title="Sin Estrategia"><LinkOffIcon sx={{ color: '#64748b' }} /></Tooltip>}
                </TableCell>

                {/* 5. NUEVO BOTÓN: IR A ANÁLISIS */}
                <TableCell align="right">
                    <Button 
                        variant="outlined" 
                        size="small" 
                        endIcon={<AnalyticsIcon />}
                        onClick={() => onAnalyze(row.id)}
                        disabled={!row.linked_ticker} 
                        sx={{ 
                            borderColor: '#334155', 
                            color: '#f8fafc', 
                            textTransform: 'none',
                            '&:hover': { borderColor: '#38bdf8', bgcolor: 'rgba(56, 189, 248, 0.05)' },
                            '&.Mui-disabled': { borderColor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.2)' }
                        }}
                    >
                        Analizar
                    </Button>
                </TableCell>
            </TableRow>

            {/* DETALLE COLAPSABLE (MANTENIDO IGUAL) */}
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0, borderBottom: 'none' }} colSpan={7}> {/* OJO: colSpan aumentado a 7 */}
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 2, ml: 8, p: 3, bgcolor: '#0f172a', borderRadius: 2, border: '1px solid #334155' }}>
                            <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, mb: 2, display: 'block', fontWeight: 700 }}>
                                Detalle de Componentes ({row.sku_count})
                            </Typography>
                            
                            {loadingSkus ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} sx={{ color: '#38bdf8' }} /></Box>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ color: '#64748b', borderBottom: '1px solid #334155' }}>SKU (Materia Prima)</TableCell>
                                            <TableCell align="right" sx={{ color: '#64748b', borderBottom: '1px solid #334155' }}>Precio Medio</TableCell>
                                            <TableCell align="right" sx={{ color: '#64748b', borderBottom: '1px solid #334155' }}>Gasto Total</TableCell>
                                            <TableCell align="right" sx={{ color: '#64748b', borderBottom: '1px solid #334155' }}>Acción</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {skuDetails.map((sku: any) => ( // Usamos any temporalmente o actualiza la interfaz FamilySkuItem
                                            <TableRow key={sku.id || sku.name} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                                {/* CORRECCIÓN: Usamos .name en lugar de .sku */}
                                                <TableCell sx={{ color: '#e2e8f0', fontFamily: 'monospace' }}>{sku.name}</TableCell>
                                                <TableCell align="right" sx={{ color: '#94a3b8' }}>
                                                    {sku.avg_price.toLocaleString('es-ES', { style: 'currency', currency: sku.currency })}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: '#cbd5e1', fontWeight: 600 }}>
                                                    {sku.total_spend.toLocaleString('es-ES', { style: 'currency', currency: sku.currency })}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Desvincular de esta familia">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => unassignMutation.mutate(sku.name)}
                                                            disabled={unassignMutation.isPending}
                                                            sx={{ color: '#475569', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.1)' } }}
                                                        >
                                                            <LinkOffIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

// --- COMPONENTE PRINCIPAL ---
const ProcurementStrategyPage: FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // 1. CARGA DE DATOS (FAMILIAS)
    const { data: families = [], isLoading: loadingFam } = useQuery({
        queryKey: ['families'],
        queryFn: async () => (await axiosInstance.get<Family[]>('/procurement/families')).data
    });

    // 2. CARGA DE INDICES
    const { data: indices = [], isLoading: loadingIdx } = useQuery({
        queryKey: ['indices'],
        queryFn: async () => (await axiosInstance.get<MarketIndex[]>('/procurement/indices')).data
    });

    // 3. NUEVO: CARGA DE STATUS GLOBAL (Para obtener el denominador real)
    const { data: statusData } = useQuery({
        queryKey: ['procurementStatus'],
        queryFn: async () => (await axiosInstance.get<ProcurementStatus>('/procurement/status')).data
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, ticker }: { id: string, ticker: string | null }) => { // <--- ACEPTAR NULL
            await axiosInstance.put(`/procurement/families/${id}`, { linked_ticker: ticker });
        },
        onSuccess: () => {
            enqueueSnackbar('Estrategia actualizada.', { variant: 'success' });
            queryClient.invalidateQueries({ queryKey: ['families'] });
        },
        onError: () => enqueueSnackbar('Error de conexión.', { variant: 'error' })
    });

    // 4. CÁLCULO DE COBERTURA REAL
    // Numerador: Gasto de familias que tienen un ticker asignado
    const linkedExposure = useMemo(() => families.filter(f => f.linked_ticker).reduce((acc, f) => acc + f.total_spend, 0), [families]);
    
    // Denominador: Gasto TOTAL Global (traído del backend). Si no ha cargado aún, usamos 1 para evitar NaN.
    const totalGlobalSpend = statusData?.total_spend_12m || 1;
    
    // Porcentaje: (Conectado / Total Real) * 100
    const coveragePercent = totalGlobalSpend > 0 ? Math.min((linkedExposure / totalGlobalSpend) * 100, 100) : 0;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
                <Box>
                    <Typography variant="h4" component="h1" sx={{ color: '#f8fafc', fontWeight: 700 }}>Estrategia de Mercado</Typography>
                    <Typography variant="body1" sx={{ color: '#94a3b8' }}>Conecta tus familias de compra con índices bursátiles.</Typography>
                </Box>
                <Paper sx={{ p: 2, bgcolor: 'rgba(56, 189, 248, 0.05)', border: '1px solid #38bdf8', borderRadius: 2, minWidth: 200 }}>
                    <Typography variant="caption" sx={{ color: '#38bdf8', textTransform: 'uppercase', fontWeight: 700 }}>Cobertura</Typography>
                    <Stack direction="row" alignItems="baseline" spacing={1}>
                        <Typography variant="h4" sx={{ color: '#f8fafc', fontWeight: 700 }}>{coveragePercent.toFixed(0)}%</Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>del gasto total</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={coveragePercent} sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.3)', '& .MuiLinearProgress-bar': { bgcolor: '#38bdf8' } }} />
                </Paper>
            </Box>

            <Paper elevation={0} sx={glassCardSx}>
                {(loadingFam || loadingIdx) && <LinearProgress sx={{ bgcolor: 'rgba(56,189,248,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#38bdf8' } }} />}
                <TableContainer sx={{ maxHeight: '70vh' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#0f172a' }}>
                                {/* 1. CELDA VACÍA PARA LA FLECHA (CORRECCIÓN AQUÍ) */}
                                <TableCell sx={{ bgcolor: '#0f172a', borderBottom: '1px solid #334155', width: 50 }} />
                                
                                {/* 2. FAMILIA */}
                                <TableCell sx={{ bgcolor: '#0f172a', color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                                    FAMILIA
                                </TableCell>
                                
                                {/* 3. GASTO */}
                                <TableCell align="right" sx={{ bgcolor: '#0f172a', color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                                    GASTO ANUAL
                                </TableCell>
                                
                                {/* 4. ITEMS */}
                                <TableCell align="center" sx={{ bgcolor: '#0f172a', color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                                    ITEMS
                                </TableCell>
                                
                                {/* 5. BENCHMARK */}
                                <TableCell sx={{ bgcolor: '#0f172a', color: '#94a3b8', borderBottom: '1px solid #334155', width: 300 }}>
                                    BENCHMARK (MERCADO)
                                </TableCell>
                                
                                {/* 6. ESTADO */}
                                <TableCell align="center" sx={{ bgcolor: '#0f172a', color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                                    ESTADO
                                </TableCell>
                                
                                {/* 7. ACCIÓN */}
                                <TableCell align="right" sx={{ bgcolor: '#0f172a', color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                                    ACCIÓN
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {families.map((family) => (
                                <FamilyRow 
                                    key={family.id} 
                                    row={family} 
                                    indices={indices} 
                                    onUpdateTicker={(id, ticker) => updateMutation.mutate({ id, ticker })} 
                                    onAnalyze={(id) => navigate(`/procurement/analysis/${id}`)} // <--- 3. PASAMOS LA NAVEGACIÓN
                                    updating={updateMutation.isPending} 
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Container>
    );
};

export default ProcurementStrategyPage;